/**
 * SelectUdaDialog
 *
 * The UDA question, asked as a dialog over the sign-in page.
 *
 * An employee can belong to more than one Urban Development Authority. In the database that
 * is not one account with two jurisdictions — ds_usr_lst_t is keyed (username, tenantid), so
 * the same person holds a separate row per UDA, each with its own positions, assignments and
 * data. Choosing here decides which of those identities the session runs as, which is why it
 * has to be answered before the role step (the answer decides which roles even exist).
 *
 * Rendered by AuthProvider rather than by the sign-in page, so the question is asked no
 * matter which entry point the password was submitted from.
 *
 * It is deliberately not dismissable by backdrop or Escape: the session is already bound to
 * whichever UDA the token defaulted to, so closing it without choosing would silently answer
 * the question. "Not now" signs the user out instead.
 *
 * Employees of a single UDA never see this; state and regional users never do either — they
 * work across UDAs by design and switch from the header.
 */

import React, { useState } from 'react';
import { useAuth, type Tenant } from '../../../../context/AuthContext';

/**
 * UDA names arrive as "Rajahmundry Urban Development Authority  (RUDA)" — the authority and
 * its acronym in one string. Split them so the card can lead with the short code and keep the
 * full name as the supporting line.
 */
const splitUdaName = (name: string, udaCode?: string | null): { code: string | null; label: string } => {
  // The server now sends the UDA name (ds_uda_lst_t) with its code beside it; the bracket
  // form is only for a name that still carries one.
  if (udaCode) return { code: udaCode, label: (name || '').replace(/\s{2,}/g, ' ').trim() };
  const match = /^(.*?)\s*\(([^)]+)\)\s*$/.exec((name || '').trim());
  if (!match) return { code: null, label: (name || '').replace(/\s{2,}/g, ' ').trim() };
  return { code: match[2].trim(), label: match[1].replace(/\s{2,}/g, ' ').trim() };
};

const SelectUdaDialog: React.FC = () => {
  const { pendingUdaSelection, selectUda, cancelUdaSelection, user } = useAuth();
  const [submittingId, setSubmittingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const udas: Tenant[] = pendingUdaSelection || [];
  if (!pendingUdaSelection || udas.length === 0) return null;

  const handleSelect = async (tenantId: number) => {
    if (submittingId !== null) return; // selectUda navigates away on success; ignore double-clicks
    setError(null);
    setSubmittingId(tenantId);
    try {
      await selectUda(tenantId);
      // selectUda performs a full document load into the role picker or the landing page.
    } catch (e: any) {
      setError(e?.message || 'Could not open that UDA. Please try again.');
      setSubmittingId(null);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="select-uda-title"
      className="fixed inset-0 z-[1400] flex items-center justify-center p-4"
    >
      {/* Backdrop is inert on purpose — see the note at the top of this file. */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" aria-hidden="true" />

      <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-gray-700 dark:bg-gray-900">
        <div className="text-center mb-5">
          <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-gray-400">
            {user?.name ? `Signed in as ${user.name}` : 'Signed in'}
          </p>
          <h2
            id="select-uda-title"
            className="mt-1 text-xl font-bold tracking-tight text-slate-900 dark:text-gray-100"
          >
            Select your UDA
          </h2>
          <div className="mx-auto mt-3 h-1 w-16 rounded-full bg-gradient-to-r from-amber-500 to-amber-600" />
          <p className="mt-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            You work at more than one authority. Choose the one you want to open — each has its
            own positions, data and records.
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <ul className="space-y-3 max-h-[45vh] overflow-y-auto">
          {udas.map((uda) => {
            const { code, label } = splitUdaName(uda.name, uda.udaCode);
            const isSubmitting = submittingId === uda.id;
            const isDisabled = submittingId !== null && !isSubmitting;
            return (
              <li key={uda.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(uda.id)}
                  disabled={submittingId !== null}
                  aria-busy={isSubmitting}
                  className={`w-full text-left rounded-xl border p-4 transition
                    border-slate-200 dark:border-gray-700
                    bg-white dark:bg-gray-900/40
                    hover:border-amber-500 hover:shadow-md
                    focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1
                    ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                    ${isSubmitting ? 'border-amber-500 shadow-md' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="shrink-0 flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      <i className="ri-government-line text-lg" aria-hidden="true"></i>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-semibold text-slate-900 dark:text-gray-100 truncate">
                        {code || label}
                      </span>
                      {code && (
                        <span className="block text-xs text-gray-600 dark:text-gray-400 truncate">
                          {label}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-amber-600 dark:text-amber-400">
                      {isSubmitting ? (
                        <i className="ri-loader-4-line animate-spin text-lg" aria-hidden="true"></i>
                      ) : (
                        <i className="ri-arrow-right-line text-lg" aria-hidden="true"></i>
                      )}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => cancelUdaSelection()}
            disabled={submittingId !== null}
            className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Not now — sign out
          </button>
          <p className="text-right text-xs text-gray-400 dark:text-gray-500">
            You can switch UDA later from the header.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SelectUdaDialog;
